import type { Phase1JobPoll } from "./phase1Job";

export type Phase3Progress = NonNullable<Phase1JobPoll["progress"]> & {
  source_count?: number;
  projects_discovered?: number;
  projects_synthesized?: number;
  main_contractor?: string;
};

export type SynthesisLogStep = {
  step?: number;
  step_total?: number;
  kind?: string;
  status?: string;
  project_name?: string;
  source_count?: number;
  projects_discovered?: number;
  delta_count?: number;
  error?: string;
};

export function phase3OverallPercent(progress: Phase3Progress | null | undefined): number {
  const total = Number(progress?.queries_queued ?? 0);
  const done = Number(progress?.queries_done ?? 0);
  if (!total) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

export function describePhase3Progress(
  progress: Phase3Progress | null | undefined,
  status: string,
): { headline: string; detail: string; phaseLabel: string } {
  const phase = progress?.phase || "starting";
  const sources = Number(progress?.source_count ?? 0);
  const discovered = Number(progress?.projects_discovered ?? 0);
  const synthesized = Number(progress?.projects_synthesized ?? 0);
  const detail =
    progress?.current_query_preview ||
    progress?.current_title ||
    (sources ? `${sources} source(s) in this bucket` : "");

  if (status === "pending") {
    return {
      headline: "Queued for PDR synthesis",
      detail: "Waiting for background worker…",
      phaseLabel: "queued",
    };
  }

  if (phase === "discovering") {
    return {
      headline: "PDR discovery (single batch)",
      detail:
        detail ||
        (sources
          ? `Routing ${sources} page(s) into project assignments…`
          : "Analyzing evidence with the PDR router…"),
      phaseLabel: "discovery",
    };
  }

  if (phase === "synthesizing") {
    return {
      headline: progress?.current_title || "Synthesizing PDR projects",
      detail:
        detail ||
        `${synthesized} updated · ${discovered} project route(s) from discovery`,
      phaseLabel: "synthesis",
    };
  }

  return {
    headline: "PDR synthesis",
    detail: detail || "Processing…",
    phaseLabel: phase,
  };
}
